// ========== TYPES ==========

export type SupportedLocale = 'en';

export interface StakeMessageContext {
  /** Native token symbol (POL, SOL, …) — used in error/hint copy */
  ticker: string;
  /** Inclusive minimum custom stake for the selected chain */
  min: number;
  /** Inclusive maximum custom stake for the selected chain */
  max: number;
}

export type StakeMessageBuilder = (ctx: StakeMessageContext) => string;

export interface StakeMessages {
  'stake.error.belowMinimum': StakeMessageBuilder;
  'stake.error.aboveMaximum': StakeMessageBuilder;
  'stake.error.invalidType': StakeMessageBuilder;
  'stake.error.mustBeInteger': StakeMessageBuilder;
  'stake.error.negative': StakeMessageBuilder;
  'stake.hint.range': StakeMessageBuilder;
}

export type StakeMessageKey = keyof StakeMessages;

// ========== MESSAGES ==========

export const STAKE_MESSAGES: Record<SupportedLocale, StakeMessages> = {
  en: {
    'stake.error.belowMinimum': ({ min, ticker }) =>
      `Stake must be at least ${min} ${ticker}`,
    'stake.error.aboveMaximum': ({ max, ticker }) =>
      `Stake cannot exceed ${max} ${ticker}`,
    'stake.error.invalidType': () => 'Invalid stake value',
    'stake.error.mustBeInteger': () => 'Stake must be a whole number',
    'stake.error.negative': () => 'Stake cannot be negative',
    'stake.hint.range': ({ min, max, ticker }) =>
      `Enter amount between ${min} and ${max} ${ticker}`,
  },
};

// ========== HELPERS ==========

const DEFAULT_CONTEXT: StakeMessageContext = {
  ticker: 'POL',
  min: 1,
  max: 1000,
};

/**
 * Gets the translated message for a stake error key, interpolated with the
 * chain-specific context (ticker, min, max).
 */
export const getStakeMessage = (
  key: StakeMessageKey | null,
  locale?: SupportedLocale,
  context: StakeMessageContext = DEFAULT_CONTEXT,
): string | null => {
  if (!key) return null;

  const lang = locale ?? 'en';
  const messages = STAKE_MESSAGES[lang] ?? STAKE_MESSAGES.en;

  const builder = messages[key] ?? STAKE_MESSAGES.en[key];
  return builder ? builder(context) : null;
};
